import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import enTranslations from './locales/en/translations.json';

export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';
export const NAMESPACES = ['translations'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const i18n = i18next.use(initReactI18next);

// Namespaces load through dynamic `import()`, not `i18next-http-backend`: that plugin fetches
// `/locales/{{lng}}/{{ns}}.json`, which Vite does not serve from `src/` and which jsdom tests would
// fail under the harness's `onUnhandledRequest: 'error'`. Dynamic import gives Vite-native
// code-splitting with neither problem.
const loaders: Record<Locale, Record<Namespace, () => Promise<{ default: object }>>> = {
  en: {
    translations: () => import('./locales/en/translations.json'),
  },
  ar: {
    translations: () => import('./locales/ar/translations.json'),
  },
};

export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Loads every namespace for a locale and registers it with i18next. */
export async function loadLocale(locale: Locale): Promise<void> {
  const entries = await Promise.all(
    NAMESPACES.map(async (ns) => [ns, (await loaders[locale][ns]()).default] as const),
  );
  for (const [ns, resource] of entries) {
    i18n.addResourceBundle(locale, ns, resource, true, true);
  }
}

export async function changeLocale(locale: Locale): Promise<void> {
  await loadLocale(locale);
  await i18n.changeLanguage(locale);
}

export async function initI18n(locale: Locale = DEFAULT_LOCALE): Promise<void> {
  if (i18n.isInitialized) {
    // Honors the requested locale even when another caller initialized first; ignoring it
    // silently is how the persisted locale gets dropped on boot.
    if (i18n.language !== locale) await changeLocale(locale);
    return;
  }

  await i18n.init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'translations',
    ns: NAMESPACES,
    // `en/translations` is bundled so the shell can render before any chunk
    // loads; everything else arrives via loadLocale below.
    resources: { en: { translations: enTranslations } },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

  await loadLocale(locale);
}
